/**
 * ScrollToBottomButton Component
 *
 * A floating action button that appears when the user scrolls up in a chat
 * and allows them to quickly scroll back to the latest messages.
 *
 * Usage:
 *   <ScrollToBottomButton
 *     containerRef={messagesContainerRef}
 *     threshold={200}
 *   />
 */

import React, { useState, useEffect, useCallback } from 'react';
import { usePrefersReducedMotion } from '@/hooks/useMediaQuery';

interface ScrollToBottomButtonProps {
  /** Reference to the scrollable container */
  containerRef: React.RefObject<HTMLElement>;
  /** Distance from bottom (in px) before showing the button */
  threshold?: number;
  /** Optional click handler override */
  onClick?: () => void;
  /** Additional class names */
  className?: string;
}

export const ScrollToBottomButton: React.FC<ScrollToBottomButtonProps> = ({
  containerRef,
  threshold = 200,
  onClick,
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Check scroll position
  const checkScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    setIsVisible(distanceFromBottom > threshold);
  }, [containerRef, threshold]);

  // Listen to scroll events
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll(); // Initial check

    return () => {
      container.removeEventListener('scroll', checkScroll);
    };
  }, [containerRef, checkScroll]);

  // Handle click
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    } else {
      const container = containerRef.current;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
      }
    }
    setUnreadCount(0);
  }, [onClick, containerRef, prefersReducedMotion]);

  if (!isVisible) return null;

  return (
    <button
      onClick={handleClick}
      className={`
        fixed bottom-24 right-4 md:right-8 z-30
        w-12 h-12
        bg-primary text-white
        rounded-full shadow-lg
        flex items-center justify-center
        hover:bg-primary/90
        focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
        transition-all duration-200
        touch-manipulation
        ${prefersReducedMotion ? '' : 'animate-bounce-subtle'}
        ${className}
      `.trim()}
      aria-label="Scroll to bottom"
    >
      {/* Down Arrow Icon */}
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 14l-7 7m0 0l-7-7m7 7V3"
        />
      </svg>

      {/* Unread Badge */}
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-bad text-white text-xs font-semibold rounded-full flex items-center justify-center">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  );
};

export default ScrollToBottomButton;
